import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const ActivityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    userName: '',
    userPhone: '',
    mpesaName: '',
    notes: ''
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchActivity();
  }, [id]);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`/api/activities/${id}`);
      setActivity(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching activity:', error);
      setLoading(false);
    }
  };

  const validatePhone = (phone) => {
    const kenyanPhoneRegex = /^(0[17]\d{8}|254[17]\d{8}|\+254[17]\d{8})$/;
    return kenyanPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!user) {
      navigate('/login');
      return;
    }

    if (!validatePhone(formData.userPhone)) {
      setError('Please enter a valid Kenyan phone number (07XX or 01XX)');
      return;
    }

    try {
      await axios.post('/api/bookings', {
        activityId: id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Error creating booking');
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!activity) return <div style={styles.error}>Activity not found</div>;

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>Booking Submitted!</h1>
          <div style={styles.paymentInstructions}>
            <h3 style={{color: '#2C2C2C', marginBottom: '1rem'}}>Complete Your Payment</h3>
            <div style={styles.paymentBox}>
              <p style={styles.paymentLabel}>Send Payment To:</p>
              <p style={styles.mpesaNumber}>0119008944</p>
              <p style={styles.amount}>Amount: KES {activity.price}</p>
            </div>
            <p style={styles.note}>
              Your booking will be confirmed within 1 hour after payment verification.
            </p>
            <p style={styles.note}>
              Questions? WhatsApp us: <strong>0119008944</strong>
            </p>
          </div>
          <button 
            onClick={() => navigate('/my-bookings')}
            className="btn btn-primary"
            style={{marginTop: '2rem'}}
          >
            View My Bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.activityHeader}>
          <img 
            src={activity.image} 
            alt={activity.name}
            style={styles.activityImage}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3E' + encodeURIComponent(activity.name) + '%3C/text%3E%3C/svg%3E';
            }}
          />
          <div style={styles.badge}>{activity.category}</div>
        </div>

        <h1 style={styles.title}>{activity.name}</h1>
        <p style={styles.description}>{activity.description}</p>

        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Day:</span>
            <span style={styles.detailValue}>{activity.day}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Time:</span>
            <span style={styles.detailValue}>{activity.time}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Duration:</span>
            <span style={styles.detailValue}>{activity.duration}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Price:</span>
            <span style={styles.priceValue}>KES {activity.price}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Spots Available:</span>
            <span style={styles.spotsValue}>{activity.availableSpots}</span>
          </div>
        </div>

        {activity.bookingOpen && activity.availableSpots > 0 ? (
          <div style={styles.bookingSection}>
            <h2 style={styles.formTitle}>Book This Activity</h2>
            
            {error && <div style={styles.errorAlert}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.userName}
                  onChange={(e) => setFormData({...formData, userName: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Salma Abdalla"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.userPhone}
                  onChange={(e) => setFormData({...formData, userPhone: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 0712345678"
                />
                <small style={styles.helpText}>Format: 07XX or 01XX</small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>M-Pesa Name *</label>
                <input
                  type="text"
                  required
                  value={formData.mpesaName}
                  onChange={(e) => setFormData({...formData, mpesaName: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., SALMA ABDALLA"
                />
                <small style={styles.helpText}>As it appears on M-Pesa (helps us verify payment)</small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Additional Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  style={styles.textarea}
                  placeholder="Any special requirements or questions?"
                  rows="3"
                />
              </div>

              <div style={styles.paymentInfo}>
                <h3 style={{color: '#2C2C2C', marginBottom: '0.5rem'}}>Payment Instructions</h3>
                <p style={{marginBottom: '0.5rem'}}>After booking, send payment to:</p>
                <p style={{fontSize: '1.3rem', fontWeight: 700, color: '#1a5632', marginBottom: '0.5rem'}}>
                  M-Pesa: 0119008944
                </p>
                <p style={{fontSize: '1.1rem', color: '#E75A7C', fontWeight: 600}}>
                  Amount: KES {activity.price}
                </p>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary"
                style={{width: '100%', padding: '1rem', fontSize: '1.1rem', marginTop: '1.5rem'}}
              >
                Submit Booking
              </button>
            </form>
          </div>
        ) : (
          <div style={styles.unavailable}>
            <h3>Booking Not Available</h3>
            <p>
              {activity.availableSpots === 0 
                ? 'This activity is currently full. Check back later!' 
                : 'Bookings are currently closed. Check back soon!'}
            </p>
            <p style={{marginTop: '1rem', color: '#1a5632', fontWeight: 600}}>
              Follow us on Instagram @glowflex.ke for updates!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '2rem auto', padding: '0 2rem' },
  loading: { textAlign: 'center', padding: '4rem', fontSize: '1.2rem' },
  error: { textAlign: 'center', padding: '4rem', color: '#F44336' },
  card: { background: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  activityHeader: { position: 'relative', height: '300px' },
  activityImage: { width: '100%', height: '100%', objectFit: 'cover' },
  badge: { position: 'absolute', top: '20px', right: '20px', background: 'rgba(255,168,185,0.95)', color: 'white', padding: '0.5rem 1.5rem', borderRadius: '25px', fontWeight: 600 },
  title: { fontSize: '2.5rem', padding: '2rem 2rem 1rem', fontFamily: 'Playfair Display, serif', color: '#1a5632' },
  description: { padding: '0 2rem 1.5rem', fontSize: '1.1rem', color: '#5A5A5A', lineHeight: 1.8 },
  detailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', padding: '0 2rem 2rem', borderBottom: '2px solid #f0f0f0' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  detailLabel: { fontSize: '0.9rem', color: '#888', fontWeight: 500 },
  detailValue: { fontSize: '1.1rem', color: '#2C2C2C', fontWeight: 600 },
  priceValue: { fontSize: '1.3rem', color: '#1a5632', fontWeight: 700 },
  spotsValue: { fontSize: '1.1rem', color: '#7AB845', fontWeight: 700 },
  bookingSection: { padding: '2rem' },
  formTitle: { fontSize: '1.8rem', marginBottom: '1.5rem', color: '#1a5632', fontFamily: 'Playfair Display, serif' },
  formGroup: { marginBottom: '1.5rem' },
  label: { display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#2C2C2C' },
  input: { width: '100%', padding: '0.875rem', border: '2px solid #ddd', borderRadius: '12px', fontSize: '1rem' },
  textarea: { width: '100%', padding: '0.875rem', border: '2px solid #ddd', borderRadius: '12px', fontSize: '1rem', resize: 'vertical' },
  helpText: { display: 'block', marginTop: '0.25rem', fontSize: '0.85rem', color: '#888' },
  paymentInfo: { background: '#f9f9f9', padding: '1.5rem', borderRadius: '12px', marginTop: '1.5rem', border: '2px solid #FFA8B9' },
  errorAlert: { background: '#ffebee', color: '#c62828', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 500 },
  unavailable: { padding: '3rem 2rem', textAlign: 'center', background: '#f5f5f5', margin: '2rem' },
  successCard: { background: 'white', padding: '3rem', borderRadius: '20px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  successIcon: { fontSize: '4rem', marginBottom: '1rem', color: '#4CAF50' },
  successTitle: { fontSize: '2rem', color: '#4CAF50', marginBottom: '2rem', fontFamily: 'Playfair Display, serif' },
  paymentInstructions: { background: '#f0f8f0', padding: '2rem', borderRadius: '15px', marginTop: '2rem' },
  paymentBox: { background: 'white', padding: '1.5rem', borderRadius: '12px', margin: '1rem 0', border: '3px solid #7AB845' },
  paymentLabel: { fontSize: '1rem', color: '#5A5A5A', marginBottom: '0.5rem' },
  mpesaNumber: { fontSize: '2rem', fontWeight: 700, color: '#1a5632', margin: '0.5rem 0' },
  amount: { fontSize: '1.5rem', fontWeight: 700, color: '#E75A7C', marginTop: '1rem' },
  note: { color: '#5A5A5A', marginTop: '1rem', fontSize: '0.95rem' }
};

export default ActivityDetails;